import { motion } from "framer-motion";
import { MousePointerClick, ClipboardList, MoonStar, UserX } from "lucide-react";

const scenarios = [
  { icon: MousePointerClick, label: "Portal inquiry", cue: "9:47 PM — arrives while you're at dinner" },
  { icon: ClipboardList, label: "Open-house sign-in", cue: "14 names — follow-up never happens" },
  { icon: MoonStar, label: "After-hours inquiry", cue: "Missed — no response until the next day" },
  { icon: UserX, label: "Buyer goes cold", cue: "Tour requested — no consistent follow-up" },
];

export default function ProblemSolution() {
  return (
    <section data-testid="problem-solution-section" className="py-20 md:py-28 bg-[#0B0F19]">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            You May Not Need More Leads.{" "}
            <span className="text-gradient">You May Be Losing the Ones You Already Have.</span>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-slate-200 leading-relaxed">
            Most real estate agents and teams don't have a lead problem. They have a response and
            follow-up problem.
          </p>
          <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">
            A buyer inquires about a listing while you're at a showing. A seller requests a home
            valuation at 9 PM. Portal leads from Zillow sit unanswered. Open-house sign-ins never
            get a follow-up. By the time someone responds, that prospect is already talking to
            another agent — and in real estate, the agent who responds first usually wins the
            conversation.
          </p>
          <p className="mt-5 text-base md:text-lg text-slate-100 font-medium leading-relaxed">
            iVIBES helps real estate professionals create a clear, consistent path from first
            inquiry to booked appointment.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {scenarios.map((s, i) => (
              <motion.div
                key={s.label}
                data-testid={`problem-scenario-${i}`}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.07, duration: 0.45 }}
                className="flex items-center gap-3.5 rounded-xl border border-white/10 bg-white/[0.03] p-4"
              >
                <div className="h-9 w-9 shrink-0 rounded-lg bg-red-500/10 text-red-300 flex items-center justify-center">
                  <s.icon size={17} strokeWidth={1.75} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white leading-tight">{s.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{s.cue}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
